import { useState } from 'react';
import { Stack } from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { AppDispatch } from 'redux/store';
import { forgotPassword } from 'redux/slices/auth/authSlice';
import Routes from 'constants/routes';
import AuthLayaout from 'pages/Auth/AuthLayout';
import { notify } from 'components/Common/Toast';
import CustomButton from 'components/Common/CustomButton';
import './Auth.css';
import TextInput from 'components/Common/TextInput';

const formSchema = yup
    .object({
        username: yup.string().email('Invalid email format').required('Email is required'),
    })
    .required();

export interface ForgotPasswordFormInputs {
    username: string;
}

export default function ForgotPassword() {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<ForgotPasswordFormInputs>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            username: '',
        },
    });

    const submit = async (data: { [x: string]: any }) => {
        setIsLoading(true);
        try {
            const res = await dispatch(forgotPassword(data.username));
            const { contextResponse } = res?.payload || {};
            if (contextResponse?.statusCode) {
                notify('A temporary password was sent to your email. Please log in with it to set a new password.', 'success');
                navigate(Routes.auth.login);
            }
        } catch (error: any) {
            notify(error.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthLayaout showBackButton>
            <div className="Auth-form">
                <Stack height={'100%'} direction={'column'} justifyContent={'space-around'}>
                    <Stack direction={'column'}>
                        <p className="Auth-form-title" style={{ marginBottom: '10px' }}>
                            Forgot Password
                        </p>
                        <p className="Auth-form-text">
                            Enter the email address associated with your account and we will send a temporary password.
                        </p>
                    </Stack>
                    <Controller
                        name="username"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                sx={{ mt: 2, mb: 2 }}
                                icon={null}
                                label="Email"
                                placeholder="Email"
                                error={!!errors?.username}
                                helperText={(errors.username as any)?.message}
                            />
                        )}
                    />

                    <CustomButton loading={isLoading} title="Send" onClick={handleSubmit(submit)} fontSize={24} />
                </Stack>
            </div>
        </AuthLayaout>
    );
}
